import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { MockTestData } from '../../models/MockTestData';
import { 
  fetchTestSession, 
  TestSessionRequest, 
  batchCreateTestSeries, 
  BatchCreateTestSeriesRequest,
  TestSessionDTO
} from '../../services/testSessionService';
import { getCachedSeriesWindows } from '../../data/seriesWindows';
import { testSeriesConfig } from '../../data/seriesConfig';

// Helper functions for the slice
const getSubjectColor = (subject: string): string => {
  const subjectConfig = testSeriesConfig.flatMap(series => series.subjects).find(s => s.name === subject);
  return subjectConfig?.color || '#4CAF50'; // Default to green if not found
};

// Convert a session DTO to MockTestData format

/**
 * The window for a series: the schedule loaded from the backend when available,
 * otherwise the bundled config. Falling back keeps the dashboard readable if the
 * packages call fails, at the cost of the dates being whatever shipped.
 */
const resolveSeriesWindow = (
  seriesName: string,
  grade: string
): { openingDate: string; closingDate: string } | undefined => {
  const loaded = grade ? getCachedSeriesWindows(grade) : null;
  if (loaded && loaded[seriesName]) {
    return loaded[seriesName];
  }
  return testSeriesConfig.find((series) => series.seriesName === seriesName);
};


const convertSessionDtoToMockTestData = (sessionDto: TestSessionDTO, subject: string): MockTestData => {
  // The grade the package belongs to, used to pick the right loaded schedule.
  const grade = sessionDto.packageName?.match(/Class-\d+/)?.[0] || '';

  // Format title from packageName if available
  let formattedTitle = sessionDto.packageName;
  if (formattedTitle) {
    formattedTitle = formattedTitle.replace(/^(Class-[0-9]+)\s*-\s*([^\s-]+)\s*-\s*/, '$1 $2 ');
  }
  
  // Status comes from the scheduled window for this series. The backend schedule is
  // authoritative; the bundled config is only a fallback for when it has not loaded.
  let testStatus: 'open' | 'upcoming' | 'closed' = 'open';
  
  // Extract series name from package name (e.g., "Mock Test - 01" from "Class-04 Science Mock Test - 01")
  const seriesMatch = sessionDto.packageName?.match(/Mock Test - \d+/);
  const seriesName = seriesMatch ? seriesMatch[0] : null;
  
  if (seriesName) {
    const seriesConfig = resolveSeriesWindow(seriesName, grade);
    
    if (seriesConfig) {
      const now = new Date();
      const openingDate = new Date(seriesConfig.openingDate);
      const closingDate = new Date(seriesConfig.closingDate);
      
      if (now < openingDate) {
        testStatus = 'upcoming';
      } else if (now > closingDate) {
        testStatus = 'closed';
      } else {
        testStatus = 'open';
      }
    }
  }
  
  // Calculate progress percentage - handle division by zero
  const totalQuestions = sessionDto.totalNoOfQuestions || 0;
  // Calculate completed questions from answers array, not currentQuestionIndex
  const completedQuestions = sessionDto.answers ? sessionDto.answers.length : 0;
  const progress = totalQuestions > 0 ? (completedQuestions / totalQuestions) * 100 : 0;
  
  // Calculate relative date display for test deadlines using seriesConfig dates
  let nextTestIn: string | undefined;
  
  if (seriesName) {
    const seriesConfig = resolveSeriesWindow(seriesName, grade);
    
    if (seriesConfig) {
      const today = new Date();
      
      if (testStatus === 'open') {
         // For open tests, calculate days until closing
         // No local end-of-day normalisation: the schedule carries an exact instant,
         // and pushing it to local midnight added a day for anyone east of UTC.
         const closingDate = new Date(seriesConfig.closingDate);
         const diffTime = closingDate.getTime() - today.getTime();
         const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
         
         if (diffDays <= 0) {
           nextTestIn = 'today';
         } else {
           nextTestIn = `${diffDays} days`;
         }
       } else if (testStatus === 'upcoming') {
         // For upcoming tests, calculate days until opening
         const openingDate = new Date(seriesConfig.openingDate);
         const diffTime = openingDate.getTime() - today.getTime();
         const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
         
         if (diffDays <= 0) {
           nextTestIn = 'today';
         } else {
           nextTestIn = `${diffDays} days`;
         }
       } else if (testStatus === 'closed') {
         // For closed tests, show when it was closed
         nextTestIn = 'closed';
       }
    } else {
      // Fallback if series config not found
      if (testStatus === 'open') {
        nextTestIn = '14 days';
      } else if (testStatus === 'upcoming') {
        nextTestIn = '7 days';
      }
    }
  } else {
    // Fallback if series name not found
    if (testStatus === 'open') {
      nextTestIn = '14 days';
    } else if (testStatus === 'upcoming') {
      nextTestIn = '7 days';
    }
  }
  
  return {
    sessionId: sessionDto.id,  // Changed from testId
    title: formattedTitle,
    subject,
    progress,
    color: getSubjectColor(subject),
    totalQuestions,
    completedQuestions,
    nextTestIn,
    status: testStatus,
  };
};

// Define the test session state shape
interface TestSessionsState {
  sessionsBySeriesAndSubject: {
    [seriesName: string]: {
      [subject: string]: {
        data: MockTestData | null;
        status: 'idle' | 'loading' | 'succeeded' | 'failed';
        error: string | null;
      };
    };
  };
}

// Initialize the state with series and subjects from config
const initialState: TestSessionsState = {
  sessionsBySeriesAndSubject: testSeriesConfig.reduce((seriesAcc, series) => {
    seriesAcc[series.seriesName] = series.subjects.reduce((subjectAcc, subject) => {
      subjectAcc[subject.name] = {
        data: null,
        status: 'idle',
        error: null
      };
      return subjectAcc;
    }, {} as TestSessionsState['sessionsBySeriesAndSubject'][string]);
    return seriesAcc;
  }, {} as TestSessionsState['sessionsBySeriesAndSubject'])
};

// Create async thunk for fetching a test session
export const fetchTestSessionData = createAsyncThunk(
  'testSessions/fetchTestSessionData',
  async (params: TestSessionRequest) => {
    return await fetchTestSession(params);
  }
);

// Create async thunk for batch creating test sessions
export const batchCreateTestSessionData = createAsyncThunk(
  'testSessions/batchCreateTestSessionData',
  async (params: BatchCreateTestSeriesRequest) => {
    return await batchCreateTestSeries(params);
  }
);

// Create the slice
const testSessionsSlice = createSlice({
  name: 'testSessions',
  initialState,
  reducers: {
    clearTestSessions: (state) => {
      // Reset all test sessions to initial state
      Object.keys(state.sessionsBySeriesAndSubject).forEach(seriesName => {
        Object.keys(state.sessionsBySeriesAndSubject[seriesName]).forEach(subject => {
          state.sessionsBySeriesAndSubject[seriesName][subject] = {
            data: null,
            status: 'idle',
            error: null
          };
        });
      });
    }
  },
  extraReducers: (builder) => {
    builder
      // Individual test session fetch handlers
      .addCase(fetchTestSessionData.pending, (state, action) => {
        const { series, subject } = action.meta.arg;
        if (state.sessionsBySeriesAndSubject[series]?.[subject]) {
          state.sessionsBySeriesAndSubject[series][subject].status = 'loading';
        }
      })
      .addCase(fetchTestSessionData.fulfilled, (state, action) => {
        const { series, subject } = action.meta.arg;
        if (state.sessionsBySeriesAndSubject[series]?.[subject]) {
          state.sessionsBySeriesAndSubject[series][subject].status = 'succeeded';
          state.sessionsBySeriesAndSubject[series][subject].data = action.payload;
          state.sessionsBySeriesAndSubject[series][subject].error = null;
        }
      })
      .addCase(fetchTestSessionData.rejected, (state, action) => {
        const { series, subject } = action.meta.arg;
        if (state.sessionsBySeriesAndSubject[series]?.[subject]) {
          state.sessionsBySeriesAndSubject[series][subject].status = 'failed';
          state.sessionsBySeriesAndSubject[series][subject].error = action.error.message || 'Failed to fetch test session';
        }
      })
      
      // Batch test session creation handlers
      .addCase(batchCreateTestSessionData.pending, (state) => {
        // Mark all sessions as loading
        Object.keys(state.sessionsBySeriesAndSubject).forEach(seriesName => {
          Object.keys(state.sessionsBySeriesAndSubject[seriesName]).forEach(subject => {
            state.sessionsBySeriesAndSubject[seriesName][subject].status = 'loading';
          });
        });
      })
      .addCase(batchCreateTestSessionData.fulfilled, (state, action) => {
        // The response structure is { studentId, sessions: { [subject]: [sessionDto, ...] } }
        const response = action.payload;
        
        // Type guard to ensure response has sessions
        type TestSeriesResponse = {
          studentId: string;
          sessions: Record<string, TestSessionDTO[]>;
        };
        
        function isTestSeriesResponse(obj: any): obj is TestSeriesResponse {
          return obj && typeof obj === 'object' && 'sessions' in obj;
        }
        
        if (isTestSeriesResponse(response)) {
          // Process each subject's session array
          Object.entries(response.sessions).forEach(([subject, sessionDtoArray]) => {
            // Type assertion to help TypeScript understand the structure
            const sessions = sessionDtoArray as TestSessionDTO[];
            
            // Process each session in the array
            sessions.forEach((sessionDto: TestSessionDTO) => {
              // Extract series name from the session data
              const seriesName = sessionDto.series;
              
              
              // Convert the DTO to our MockTestData format
              const mockTestData = convertSessionDtoToMockTestData(sessionDto, subject);
              
              // Create series/subject structure if it doesn't exist, then update
              if (!state.sessionsBySeriesAndSubject[seriesName]) {
                state.sessionsBySeriesAndSubject[seriesName] = {};
              }
              if (!state.sessionsBySeriesAndSubject[seriesName][subject]) {
                state.sessionsBySeriesAndSubject[seriesName][subject] = {
                  data: null,
                  status: 'idle',
                  error: null
                };
              }

              state.sessionsBySeriesAndSubject[seriesName][subject] = {
                data: mockTestData,
                status: 'succeeded',
                error: null
              };
            });
          });
        } else {
          // No sessions data in response
        }
      })
      .addCase(batchCreateTestSessionData.rejected, (state, action) => {
        // Mark all as failed with the error
        Object.keys(state.sessionsBySeriesAndSubject).forEach(seriesName => {
          Object.keys(state.sessionsBySeriesAndSubject[seriesName]).forEach(subject => {
            state.sessionsBySeriesAndSubject[seriesName][subject].status = 'failed';
            state.sessionsBySeriesAndSubject[seriesName][subject].error = 
              action.error.message || 'Failed to create test sessions in batch';
          });
        });
      });
  }
});

export const { clearTestSessions } = testSessionsSlice.actions;
export default testSessionsSlice.reducer;

// Selector to get a specific test session
export const selectTestSession = (state: { testSessions: TestSessionsState }, seriesName: string, subject: string) => {
  return state.testSessions.sessionsBySeriesAndSubject[seriesName]?.[subject] || { 
    data: null, 
    status: 'idle', 
    error: null 
  };
};
